import React, { useCallback, useState } from 'react'
import { css } from '@emotion/core'
import { DangerButton, Button, DialogOverlay, DialogOverlayFooter, DialogOverlayActions } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { PersonRemove, SocialLike } from '@fs/zion-icon'
import ChooseRecordOverlayStepOne from './ChooseRecordOverlayStepOne'
import ChooseRecordOverlayStepTwo from './ChooseRecordOverlayStepTwo'

const stepXof2Css = css`
  align-self: center;
`

export default function ChooseRecordOverlay({
  overlay,
  onCancel,
  person,
  otherPerson,
  record1Relationships,
  record2Relationships,
  setMovingPersonToOtherRecord,
}) {
  const [t] = useTranslation()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedRecord, setSelectedRecord] = useState('current')
  const existingRelationships = selectedRecord === 'current' ? record2Relationships : record1Relationships
  const dialogTitle =
    currentStep === 1 || existingRelationships.length > 0
      ? t('edit-relationships.choose-record-overlay.before-adding', 'Before adding this relationship')
      : t('edit-relationships.choose-record-overlay.approve-change', 'Approve the change')

  const confirmationHandler = useCallback(() => {
    overlay.close()
    setMovingPersonToOtherRecord(selectedRecord === 'other')
    setSelectedRecord('current')
    setCurrentStep(1)
  }, [overlay, setMovingPersonToOtherRecord, selectedRecord])

  return (
    <DialogOverlay {...overlay} title={dialogTitle} size="lg" nonClosable>
      {currentStep === 1 && (
        <ChooseRecordOverlayStepOne
          record1Relationships={record1Relationships}
          record2Relationships={record2Relationships}
          person={person}
          otherPerson={otherPerson}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
        />
      )}
      {currentStep === 2 && (
        <ChooseRecordOverlayStepTwo
          personStaying={selectedRecord === 'current' ? person : otherPerson}
          personBeingMoved={selectedRecord === 'current' ? otherPerson : person}
          record1Relationships={record1Relationships}
          record2Relationships={record2Relationships}
          selectedRecord={selectedRecord}
        />
      )}
      <DialogOverlayFooter>
        {currentStep === 1 && (
          <DialogOverlayActions alignY="middle">
            <Button
              onClick={() => {
                overlay.close()
                setSelectedRecord('current')
                if (onCancel) {
                  onCancel()
                }
              }}
              linkName="cancel-move-person-to-other-record-dialog"
              data-testid="choose-record-overlay-cancel"
            >
              {t('edit-relationships.choose-record-overlay.cancel.button', 'Cancel')}
            </Button>
            <span css={stepXof2Css}>
              {t('edit-relationships.choose-record-overlay.stepXof2', 'Step {step} of 2', { step: '1' })}
            </span>
            <Button
              onClick={() => {
                setCurrentStep(2)
              }}
              disabled={!selectedRecord}
              emphasis="high"
              linkName="next-step-move-person-to-other-record-dialog"
              data-testid="choose-record-overlay-next"
            >
              {t('edit-relationships.choose-record-overlay.next.button', 'Next')}
            </Button>
          </DialogOverlayActions>
        )}
        {currentStep === 2 && (
          <DialogOverlayActions alignY="middle">
            <Button
              onClick={() => {
                setCurrentStep(1)
              }}
              data-testid="choose-record-overlay-back"
              linkName="back-button-move-person-to-other-record-dialog"
            >
              {t('edit-relationships.choose-record-overlay.back.button', 'Back')}
            </Button>
            <span css={stepXof2Css}>
              {t('edit-relationships.choose-record-overlay.stepXof2', 'Step {step} of 2', { step: '2' })}
            </span>
            {existingRelationships.length > 0 && (
              <DangerButton
                onClick={confirmationHandler}
                Icon={PersonRemove}
                data-testid="choose-record-overlay-approve-removal"
                linkName="approve-relationship-removal-move-person-to-other-record-dialog"
              >
                {t('edit-relationships.choose-record-overlay.remove.button', 'Approve Removal')}
              </DangerButton>
            )}
            {existingRelationships.length === 0 && (
              <Button
                emphasis="high"
                Icon={SocialLike}
                onClick={confirmationHandler}
                data-testid="choose-record-overlay-approve"
                linkName="approve-changes-move-person-to-other-record-dialog"
              >
                {t('edit-relationships.choose-record-overlay.approve.button', 'Approve')}
              </Button>
            )}
          </DialogOverlayActions>
        )}
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
